/**
 * Shared registration taxonomy — used by both /register and /invite/:token
 * so the two flows stay 1-to-1 in sync (DJ stays under Entertainment Pros,
 * Priest under Ceremony Pros, etc.).
 *
 * Icons are *not* exported here so JSX-using pages can map by `iconKey`
 * to whichever icon library version they import. This keeps the data
 * module pure (zero React deps) and friendly to tests.
 */

export const VENDOR_CATEGORIES = [
  { value: 'photographer', label: 'Photographer' },
  { value: 'caterer', label: 'Caterer' },
  { value: 'decorator', label: 'Decorator' },
  { value: 'birthday_decorator', label: 'Birthday Decorator' },
  { value: 'nathaswaram_music', label: 'Celebrate With Music' },
  { value: 'return_gift', label: 'Return Gift / Muhurtham Pai' },
  { value: 'flowers_pooja', label: 'Flowers & Pooja Items' },
  { value: 'cake_vendor', label: 'Cake Vendor / Bakery' },
];

export const PROFESSIONAL_GROUPS = [
  {
    id: 'catering_staff',
    label: 'Catering Staff',
    items: [
      { value: 'chef', label: 'Chef' },
      { value: 'biryani_specialist', label: 'Biryani Specialist' },
      { value: 'assistant_chef', label: 'Assistant Chef' },
      { value: 'server_waiter', label: 'Server / Waiter' },
      { value: 'catering_team', label: 'Catering Team' },
    ],
  },
  {
    id: 'ceremony_pros',
    label: 'Ceremony Pros',
    items: [
      { value: 'priest', label: 'Priest / Purohit' },
    ],
  },
  {
    id: 'media_pros',
    label: 'Media Pros',
    items: [
      { value: 'photographer', label: 'Photographer' },
      { value: 'videographer', label: 'Videographer' },
    ],
  },
  {
    id: 'beauty_decor',
    label: 'Beauty & Decor Pros',
    items: [
      { value: 'makeup_artist', label: 'Makeup Artist' },
      { value: 'mehndi_artist', label: 'Mehndi Artist' },
      { value: 'choreographer', label: 'Choreographer' },
      { value: 'decoration_team', label: 'Decoration Team' },
    ],
  },
];

export const ENTERTAINMENT_GROUP = {
  id: 'entertainment',
  label: 'Entertainment Professionals',
  items: [
    { value: 'magician', label: 'Magician' },
    { value: 'standup_comedian', label: 'Standup Comedian' },
    { value: 'anchor_emcee', label: 'Anchor / Emcee' },
    { value: 'dancer', label: 'Dancer / Dance Troupe' },
    { value: 'costume_performer', label: 'Costume Performer' },
    { value: 'buffoon', label: 'Buffoon / Clown' },
    { value: 'singer', label: 'Singer / Vocalist' },
    { value: 'singer_karaoke', label: 'Singer with Karaoke' },
    { value: 'mimicry_artist', label: 'Mimicry Artist' },
    { value: 'musician', label: 'Musician / Instrument Player' },
    { value: 'dj', label: 'DJ' },
  ],
};

export const PRIEST_TITLE_GROUPS = [
  { tradition: 'hindu',     label: 'Hindu Wedding',     sub: 'Hindu Priest',      titles: ['Iyer', 'Pandit', 'Purohit', 'Gurukkal', 'Sastri', 'Swami'] },
  { tradition: 'christian', label: 'Christian Wedding', sub: 'Christian Officiant', titles: ['Priest', 'Father', 'Pastor', 'Reverend', 'Minister'] },
  { tradition: 'muslim',    label: 'Muslim Wedding',    sub: 'Nikah Officiant',   titles: ['Imam', 'Qazi', 'Maulana', 'Mufti', 'Sheikh'] },
];

/**
 * Format an earnings band for display.
 *  40000 → "₹40K", 100000 → "₹1L", 5000000 → "₹50L", 10000000 → "₹1Cr"
 */
export const fmtINR = (n) => {
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(n % 10000000 === 0 ? 0 : 1).replace(/\.0$/, '')}Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(n % 100000 === 0 ? 0 : 1).replace(/\.0$/, '')}L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(0)}K`;
  return `₹${n}`;
};

/**
 * `iconKey` is resolved by each consumer page to whichever icon set it has
 * imported. Keeps this module pure.
 */
export const ROLES = [
  { key: 'customer',                   title: 'Customer',        desc: 'I want to book vendors & services',                              iconKey: 'user',        role: 'customer',     category: null },
  { key: 'vendor',                     title: 'Vendor',          desc: 'Photographer, caterer, decorator, music, flowers, return gifts', iconKey: 'storefront',  role: 'vendor',       category: null,    earnings: { min: 40000,  max: 1000000,  unit: 'event' } },
  { key: 'venue_provider',             title: 'Venue Provider',  desc: 'I rent out venues for events',                                   iconKey: 'buildings',   role: 'vendor',       category: 'venue', earnings: { min: 100000, max: 5000000,  unit: 'event' } },
  { key: 'professional',               title: 'Professional',    desc: 'Chef, priest, photographer, makeup artist',                      iconKey: 'briefcase',   role: 'professional', category: null,    earnings: { min: 4000,   max: 100000,   unit: 'event' } },
  { key: 'entertainment_professional', title: 'Entertainment Pro', desc: 'Magician, DJ, dancer, anchor, costume artist',                 iconKey: 'star',        role: 'professional', category: null,    earnings: { min: 4000,   max: 100000,   unit: 'event' } },
];

/**
 * Build a flat searchable index across every role + category so a user can
 * just type "magician" or "venue" and we route them automatically. Used by
 * both /register and /invite/:token search bars.
 */
export const SEARCH_INDEX = (() => {
  const idx = [];
  for (const v of VENDOR_CATEGORIES) {
    idx.push({ roleKey: 'vendor', label: v.label, category: v.value, keywords: v.label.toLowerCase() });
  }
  idx.push({ roleKey: 'venue_provider', label: 'Venue / Hall / Mandapam', category: 'venue', keywords: 'venue hall mandapam convention banquet garden hotel villa rooftop' });
  for (const g of PROFESSIONAL_GROUPS) {
    for (const it of g.items) {
      idx.push({ roleKey: 'professional', label: it.label, category: it.value, keywords: `${it.label.toLowerCase()} ${g.label.toLowerCase()}` });
    }
  }
  for (const it of ENTERTAINMENT_GROUP.items) {
    idx.push({ roleKey: 'entertainment_professional', label: it.label, category: it.value, keywords: `${it.label.toLowerCase()} entertainment` });
  }
  return idx;
})();
